import React, { useState } from 'react';
import { SectionWrapper } from './SectionWrapper';
import { Calculator, ArrowRightLeft, ShieldCheck, Layers, Percent, TrendingDown, Coins } from 'lucide-react';
const FormulaBlock = ({
  formula,
  label
}: {
  formula: string;
  label: string;
}) => <div className="bg-slate-900 rounded-lg p-4 flex flex-col gap-2" data-id="element-347">
    <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold" data-id="element-348">
      {label}
    </span>
    <code className="font-mono text-sm text-indigo-300" data-id="element-349">{formula}</code>
  </div>;
const pricingModels = [{
  id: 'factor',
  name: 'Fixed Factor Rate',
  icon: Coins,
  summary: 'Single multiplier applied to principal at origination. Total repayment is known upfront.',
  bestFor: 'Short-duration cashflow loans (3–12 months) to merchants who prefer pricing certainty.',
  formula: 'Total Repayment = Principal × Factor Rate',
  example: 'SGD 40,000 × 1.15 = SGD 46,000 total',
  color: 'bg-emerald-50 text-emerald-700 border-emerald-200'
}, {
  id: 'simple',
  name: 'Simple Interest',
  icon: Percent,
  summary: 'Interest accrues on the original principal only. No compounding. Predictable interest expense.',
  bestFor: 'Medium-term facilities (6–18 months) where the bank wants traditional APR disclosure.',
  formula: 'Interest = Principal × APR × (Days ÷ 365)',
  example: 'SGD 40,000 × 14% × (300 ÷ 365) = SGD 4,603 interest',
  color: 'bg-indigo-50 text-indigo-700 border-indigo-200'
}, {
  id: 'amortizing',
  name: 'Amortizing Principal + Interest',
  icon: TrendingDown,
  summary: 'Interest accrues on the declining balance. As principal is repaid via holdback, interest expense reduces.',
  bestFor: 'Longer-tenor loans (12–36 months) on the bank balance sheet requiring standard accounting treatment.',
  formula: 'Interest_t = Outstanding Balance_t × (APR ÷ 365)',
  example: 'Daily accrual on declining balance; standard loan amortization schedule',
  color: 'bg-amber-50 text-amber-700 border-amber-200'
}, {
  id: 'hybrid',
  name: 'Hybrid / Custom',
  icon: Layers,
  summary: 'Combinations are configurable per bank: origination fee + interest, tiered pricing by tenor, promotional rates for top-ups.',
  bestFor: 'Banks with specific product positioning, regulatory disclosure requirements, or risk-based pricing models.',
  formula: 'Configurable per policy template',
  example: 'e.g. 2% origination fee + 12% simple interest, capped at 18 months',
  color: 'bg-slate-100 text-slate-700 border-slate-300'
}];
export function Layer5LoanSizing() {
  const [activeModel, setActiveModel] = useState('factor');
  const current = pricingModels.find(m => m.id === activeModel)!;
  const calculationSteps = [{
    param: 'Loan amount selected',
    value: 'SGD 40,000'
  }, {
    param: 'Pricing model',
    value: 'Fixed factor rate · 1.15'
  }, {
    param: 'Total repayment amount',
    value: 'SGD 46,000',
    highlight: true
  }, {
    param: 'Target repayment window',
    value: '10 months'
  }, {
    param: 'Avg monthly GMV',
    value: 'SGD 45,000'
  }, {
    param: 'Avg daily GMV (÷30)',
    value: 'SGD 1,500'
  }, {
    param: 'Blended MDR rate',
    value: '1.5%'
  }, {
    param: 'Daily MDR revenue to PSP',
    value: 'SGD 22.50'
  }, {
    param: 'Required daily repayment',
    value: 'SGD 46,000 ÷ (10 × 30) = SGD 153'
  }, {
    param: 'Holdback rate required',
    value: 'SGD 153 ÷ SGD 1,500 = ~10.2%'
  }, {
    param: 'Holdback rate applied',
    value: '10%',
    highlight: true,
    final: true
  }];
  const bands = [{
    tier: 'A (Platinum)',
    default: '8–10%',
    floor: '5%',
    ceiling: '15%'
  }, {
    tier: 'B (Gold)',
    default: '10–12%',
    floor: '6%',
    ceiling: '18%'
  }, {
    tier: 'C (Silver)',
    default: '12–15%',
    floor: '8%',
    ceiling: '20%'
  }, {
    tier: 'D (Bronze)',
    default: '15–18%',
    floor: '10%',
    ceiling: '22%'
  }];
  return <SectionWrapper title="Layer 5: Loan Sizing & Repayment" subtitle="Pomelo's settlement infrastructure becomes the repayment mechanism — eliminating collections overhead. Pricing is flexible: banks configure their preferred loan structure, from fixed factor rates to amortizing interest-bearing principal." data-id="element-350">
      {/* Pricing Models */}
      <div data-id="element-351">
        <div className="flex items-center gap-2 mb-4" data-id="element-352">
          <Layers className="w-5 h-5 text-emerald-600" data-id="element-353" />
          <h2 className="text-xl font-semibold text-slate-900" data-id="element-354">
            Flexible Pricing Models
          </h2>
        </div>
        <p className="text-slate-600 mb-6 max-w-3xl leading-relaxed text-sm" data-id="element-355">
          The engine supports multiple loan structures so partner banks can
          align with their existing product taxonomy, regulatory disclosure
          regime, and risk appetite. The choice of pricing model is set per
          product, not per merchant.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4" data-id="element-356">
          {pricingModels.map(m => {
          const Icon = m.icon;
          const active = m.id === activeModel;
          return <button key={m.id} onClick={() => setActiveModel(m.id)} className={`p-3 rounded-lg border-2 text-left transition-all ${active ? 'border-emerald-400 bg-emerald-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-357">
                <Icon className={`w-4 h-4 mb-2 ${active ? 'text-emerald-600' : 'text-slate-500'}`} data-id="element-358" />
                <p className={`text-xs font-semibold ${active ? 'text-slate-900' : 'text-slate-600'}`} data-id="element-359">
                  {m.name}
                </p>
              </button>;
        })}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-360">
          <div className="flex items-start gap-3 mb-4" data-id="element-361">
            <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded border ${current.color}`} data-id="element-362">
              {current.name}
            </span>
          </div>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-363">
            {current.summary}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" data-id="element-364">
            <div data-id="element-365">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5" data-id="element-366">
                Best for
              </p>
              <p className="text-slate-700 leading-relaxed" data-id="element-367">
                {current.bestFor}
              </p>
            </div>
            <div data-id="element-368">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5" data-id="element-369">
                Formula
              </p>
              <code className="font-mono text-xs text-indigo-700 bg-indigo-50 px-2 py-1 rounded inline-block" data-id="element-370">
                {current.formula}
              </code>
            </div>
            <div data-id="element-371">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5" data-id="element-372">
                Example
              </p>
              <p className="text-slate-700 font-mono text-xs leading-relaxed" data-id="element-373">
                {current.example}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Loan Ceiling & Holdback Formulas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-374">
        <FormulaBlock label="Loan Ceiling" formula="Ceiling = Tier multiplier × Weighted avg monthly GMV" data-id="element-375" />
        <FormulaBlock label="Holdback %" formula="Holdback % = Required daily repayment ÷ (Expected daily GMV × MDR rate)" data-id="element-376" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-377">
        {/* Worked Example Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-378">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2" data-id="element-379">
            <Calculator className="w-5 h-5 text-indigo-600" data-id="element-380" />
            <h3 className="font-semibold text-slate-900" data-id="element-381">
              Worked Example · Factor Rate Model
            </h3>
          </div>
          <table className="w-full text-sm text-left" data-id="element-382">
            <tbody className="divide-y divide-slate-100" data-id="element-383">
              {calculationSteps.map((step, i) => <tr key={i} className={step.final ? 'bg-emerald-50' : 'hover:bg-slate-50'} data-id="element-384">
                  <td className={`px-6 py-3 ${step.final ? 'font-bold text-emerald-900' : 'text-slate-600'}`} data-id="element-385">
                    {step.param}
                  </td>
                  <td className={`px-6 py-3 font-mono text-right ${step.highlight ? 'font-bold text-emerald-700' : 'text-slate-900'}`} data-id="element-386">
                    {step.value}
                  </td>
                </tr>)}
            </tbody>
          </table>
          <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-500" data-id="element-387">
            For simple interest or amortizing structures, daily repayment is
            sized against the full debt service schedule using the same holdback
            approach.
          </div>
        </div>

        <div className="space-y-8" data-id="element-388">
          {/* Flexible Mechanics */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-389">
            <div className="flex items-center gap-2 mb-4" data-id="element-390">
              <ArrowRightLeft className="w-5 h-5 text-teal-600" data-id="element-391" />
              <h3 className="font-semibold text-slate-900" data-id="element-392">
                Flexible Repayment Mechanics
              </h3>
            </div>
            <p className="text-sm text-slate-600 mb-4" data-id="element-393">
              Regardless of pricing model, the MDR holdback adjusts repayment
              velocity to business performance:
            </p>
            <ul className="space-y-3 text-sm" data-id="element-394">
              <li className="flex items-start gap-3" data-id="element-395">
                <div className="mt-0.5 w-2 h-2 rounded-full bg-teal-500 flex-shrink-0" data-id="element-396" />
                <span className="text-slate-700" data-id="element-397">
                  If GMV runs higher than modelled, the merchant repays faster —
                  and on interest-bearing structures, total interest paid is
                  reduced.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-398">
                <div className="mt-0.5 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" data-id="element-399" />
                <span className="text-slate-700" data-id="element-400">
                  If GMV drops, repayment slows — but the merchant is never
                  forced to make a fixed payment from funds they don't have.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-401">
                <div className="mt-0.5 w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" data-id="element-402" />
                <span className="text-slate-700 font-medium" data-id="element-403">
                  This is the core merchant-friendly differentiator versus a
                  traditional term loan with fixed monthly instalments.
                </span>
              </li>
            </ul>
          </div>

          {/* Holdback Bands */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-404">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2" data-id="element-405">
              <ShieldCheck className="w-5 h-5 text-slate-600" data-id="element-406" />
              <h3 className="font-semibold text-slate-900" data-id="element-407">
                Holdback Rate Bands
              </h3>
            </div>
            <table className="w-full text-sm text-left" data-id="element-408">
              <thead className="bg-slate-50/50 text-slate-500 font-medium border-b border-slate-100" data-id="element-409">
                <tr data-id="element-410">
                  <th className="px-4 py-3" data-id="element-411">Tier</th>
                  <th className="px-4 py-3" data-id="element-412">Default</th>
                  <th className="px-4 py-3" data-id="element-413">Floor</th>
                  <th className="px-4 py-3" data-id="element-414">Ceiling</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-415">
                {bands.map((b, i) => <tr key={i} data-id="element-416">
                    <td className="px-4 py-3 font-medium text-slate-900" data-id="element-417">
                      {b.tier}
                    </td>
                    <td className="px-4 py-3 font-mono text-indigo-600" data-id="element-418">
                      {b.default}
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-500" data-id="element-419">
                      {b.floor}
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-500" data-id="element-420">
                      {b.ceiling}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SectionWrapper>;
}